import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Plus, Loader2, PackagePlus, BadgeCheck, AlertTriangle } from 'lucide-react';
import { market, stores as storesApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import ImageUploader from './ImageUploader';
import BrandEditor from './BrandEditor';
import { NationalOrders, CommissionStatement } from './SellerNationalOrders';

const inp = 'w-full rounded-lg border border-border px-3 py-2 text-sm bg-white';
const EMPTY_P = { name: '', brand: '', manufacturer: '', category: '', description: '', images: [], video_url: '', mrp: '', gst_rate: '18', hsn: '', barcode: '', country_of_origin: 'India', warranty: '', weight_g: '', length_cm: '', width_cm: '', height_cm: '', returnable: true, specs: '' };
const EMPTY_O = { price: '', stock: '10', sku: '', dispatch_days: '2' };
const STATUS_CLS = { approved: 'text-[#1B5E20]', pending: 'text-[#E67E22]', rejected: 'text-[#B71C1C]' };

function OfferFields({ o, setO, stores, storeId, setStoreId, expiry }) {
  const f = (k, label, type = 'text') => (
    <label className="block"><span className="text-xs text-ink/60">{label}</span>
      <input data-testid={`offer-${k}`} type={type} value={o[k] || ''} onChange={e => setO({ ...o, [k]: e.target.value })} className={inp} required/></label>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      <label className="block"><span className="text-xs text-ink/60">Your shop</span>
        <select data-testid="offer-store" value={storeId} onChange={e => setStoreId(e.target.value)} className={inp}>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select></label>
      {f('price', 'Selling price ₹', 'number')}{f('stock', 'Stock', 'number')}{f('sku', 'Your SKU')}{f('dispatch_days', 'Ships in (days)', 'number')}
      {expiry && <>{f('use_by_date', 'Use-by date', 'date')}{f('batch_no', 'Batch no.')}</>}
    </div>
  );
}

const EXPIRY_CATS = ['Grocery & Gourmet', 'Health & Wellness'];
const offerBody = (o, storeId) => ({ store_id: storeId, price: Number(o.price), stock: Number(o.stock), sku: o.sku.trim(), dispatch_days: Number(o.dispatch_days), ...(o.use_by_date ? { use_by_date: o.use_by_date, batch_no: o.batch_no } : {}) });
const errMsg = (e) => { const d = e?.response?.data?.detail; return Array.isArray(d) ? d[0]?.msg : d || 'Failed'; };

function SellExisting({ stores, onDone }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [pick, setPick] = useState(null);
  const [o, setO] = useState(EMPTY_O);
  const [storeId, setStoreId] = useState(stores[0]?.id);
  const [busy, setBusy] = useState(false);
  const search = (e) => { e.preventDefault(); market.search({ q }).then(d => setRes(d.items)); };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await market.addOffer(pick.id, offerBody(o, storeId)); toast.success('You are now selling this product'); setPick(null); setO(EMPTY_O); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="card-soft p-4 space-y-3" data-testid="sell-existing">
      <div className="font-heading font-bold flex items-center gap-2"><Search className="w-4 h-4"/>Sell a product already in the catalogue</div>
      <form onSubmit={search} className="flex gap-2">
        <input data-testid="sell-existing-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, brand or barcode" className={inp}/>
        <button data-testid="sell-existing-search-btn" className="btn-outline text-sm">Search</button>
      </form>
      {res && res.length === 0 && <p className="text-sm text-ink/60">Nothing found. Add it as a new product below.</p>}
      {res && res.map(p => (
        <div key={p.id} className="flex items-center gap-3 border-t border-border pt-2 text-sm">
          <img src={p.images?.[0]} alt="" className="w-10 h-10 rounded object-cover"/>
          <span className="flex-1">{p.brand} · {p.name} <span className="text-ink/50">(MRP ₹{p.mrp}, {p.offer_count} sellers)</span></span>
          <button data-testid={`sell-this-${p.id}`} onClick={() => setPick(p)} className="btn-primary !py-1 text-xs">Sell this</button>
        </div>
      ))}
      {pick && (
        <form onSubmit={save} className="space-y-2 rounded-xl bg-[#FFF8EE] p-3">
          <div className="text-sm font-semibold">Selling: {pick.name}</div>
          <OfferFields o={o} setO={setO} stores={stores} storeId={storeId} setStoreId={setStoreId} expiry={EXPIRY_CATS.includes(pick.category)}/>
          <button data-testid="sell-existing-submit" disabled={busy} className="btn-primary text-sm">{busy && <Loader2 className="w-4 h-4 animate-spin"/>}Start selling</button>
        </form>
      )}
    </section>
  );
}

function NewListing({ stores, categories, onDone }) {
  const [p, setP] = useState(EMPTY_P);
  const [o, setO] = useState(EMPTY_O);
  const [storeId, setStoreId] = useState(stores[0]?.id);
  const [dupes, setDupes] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP(x => ({ ...x, [k]: v }));
  const check = () => { if (p.name.length > 3 || p.barcode) market.match({ name: p.name, brand: p.brand, barcode: p.barcode }).then(d => setDupes(d.items)).catch(() => {}); };
  const f = (k, label, extra = {}) => (
    <label className="block"><span className="text-xs text-ink/60">{label}</span>
      <input data-testid={`listing-${k}`} value={p[k]} onChange={e => set(k, e.target.value)} className={inp} {...extra}/></label>
  );
  const save = async (e) => {
    e.preventDefault();
    if (!p.images.length) return toast.error('Add at least one product photo');
    const specs = Object.fromEntries(p.specs.split('\n').map(l => l.split(':')).filter(a => a.length >= 2 && a[0].trim()).map(([k, ...v]) => [k.trim(), v.join(':').trim()]));
    const num = (v) => (v === '' ? null : Number(v));
    setBusy(true);
    try {
      await market.createListing({ product: { ...p, specs, mrp: Number(p.mrp), gst_rate: Number(p.gst_rate), weight_g: num(p.weight_g), length_cm: num(p.length_cm), width_cm: num(p.width_cm), height_cm: num(p.height_cm) }, offer: offerBody(o, storeId) });
      toast.success('Submitted for approval. It goes live in the catalogue once approved.');
      setP(EMPTY_P); setO(EMPTY_O); setDupes([]); onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="card-soft p-4 space-y-3" data-testid="new-listing-form">
      <div className="font-heading font-bold flex items-center gap-2"><PackagePlus className="w-4 h-4"/>Add a new product to the catalogue</div>
      <div className="grid md:grid-cols-3 gap-2">
        {f('name', 'Product name *', { required: true, onBlur: check })}{f('brand', 'Brand *', { required: true, onBlur: check })}{f('manufacturer', 'Manufacturer')}
        <label className="block"><span className="text-xs text-ink/60">Category *</span>
          <select data-testid="listing-category" required value={p.category} onChange={e => set('category', e.target.value)} className={inp}>
            <option value="">Choose…</option>{categories.map(c => <option key={c}>{c}</option>)}
          </select></label>
        {f('mrp', 'MRP ₹ *', { required: true, type: 'number', step: '0.01' })}
        <label className="block"><span className="text-xs text-ink/60">GST % *</span>
          <select data-testid="listing-gst_rate" value={p.gst_rate} onChange={e => set('gst_rate', e.target.value)} className={inp}>{['0', '5', '12', '18', '28'].map(g => <option key={g}>{g}</option>)}</select></label>
        {f('hsn', 'HSN code *', { required: true, pattern: '\\d{4,8}' })}{f('barcode', 'Barcode (EAN/UPC)', { onBlur: check })}{f('country_of_origin', 'Country of origin *', { required: true })}
        {f('warranty', 'Warranty (e.g. 1 year manufacturer)')}{f('weight_g', 'Shipping weight (g)', { type: 'number' })}{f('video_url', 'Video link')}
        {f('length_cm', 'Length (cm)', { type: 'number' })}{f('width_cm', 'Width (cm)', { type: 'number' })}{f('height_cm', 'Height (cm)', { type: 'number' })}
      </div>
      {dupes.length > 0 && (
        <div data-testid="listing-duplicates" className="rounded-lg bg-[#FFF3E0] p-3 text-sm space-y-1">
          <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-[#D35400]"/>Similar products already exist. Use "Sell this" above instead of a duplicate:</div>
          {dupes.map(d => <div key={d.id}>• {d.brand} · {d.name} ({d.status})</div>)}
        </div>
      )}
      <textarea data-testid="listing-description" value={p.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Description" className={inp}/>
      <textarea data-testid="listing-specs" value={p.specs} onChange={e => set('specs', e.target.value)} rows={3} placeholder={'Specifications, one per line\nColour: Black\nCapacity: 1.5 L'} className={inp}/>
      <label className="flex items-center gap-2 text-sm"><input data-testid="listing-returnable" type="checkbox" checked={p.returnable} onChange={e => set('returnable', e.target.checked)}/>Customers can return this product</label>
      <div className="flex flex-wrap gap-2">
        {p.images.map(u => <img key={u} src={u} alt="" className="w-14 h-14 rounded-lg object-cover"/>)}
        {p.images.length < 6 && <ImageUploader value="" onChange={u => u && set('images', [...p.images, u])} label="Add photo" testId="listing-image"/>}
      </div>
      <div className="text-sm font-semibold pt-2">Your offer</div>
      <OfferFields o={o} setO={setO} stores={stores} storeId={storeId} setStoreId={setStoreId} expiry={EXPIRY_CATS.includes(p.category)}/>
      <button data-testid="listing-submit" disabled={busy} className="btn-primary text-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}Submit for approval</button>
    </form>
  );
}

export default function SellerCatalogTab() {
  const { user } = useAuth();
  const [stores, setStores] = useState(null);
  const [meta, setMeta] = useState(null);
  const [mine, setMine] = useState([]);
  const loadMine = useCallback(() => market.myOffers().then(d => setMine(d.items)).catch(() => {}), []);
  useEffect(() => {
    storesApi.myStores().then(setStores).catch(() => setStores([]));
    market.meta().then(setMeta).catch(() => {});
    loadMine();
  }, [loadMine]);

  if (!stores || !meta) return <Loader2 className="w-6 h-6 animate-spin mt-6 text-[#E67E22]"/>;
  return (
    <div className="mt-5 space-y-4" data-testid="seller-catalog-tab">
      {!user?.seller_type ? (
        <div data-testid="seller-type-cta" className="rounded-xl border border-[#E67E22]/40 bg-[#FFF8EE] p-4 text-sm">
          Get verified as a <b>Manufacturer, Brand Owner, Authorised Distributor or Retail Seller</b> to earn a trust badge on all your listings.
          <Link to="/partner/register" className="ml-2 underline font-semibold text-[#D35400]">Register now</Link>
        </div>
      ) : (
        <div className="text-sm inline-flex items-center gap-1 text-[#1B5E20] font-semibold" data-testid="seller-type-badge"><BadgeCheck className="w-4 h-4"/>Verified {user.seller_type.replace('_', ' ')}</div>
      )}
      <NationalOrders/>
      <CommissionStatement/>
      <BrandEditor/>
      {stores.length === 0 ? (
        <p className="text-sm text-ink/70" data-testid="catalog-need-store">Create your shop in the Inventory tab first, then come back to list products across India.</p>
      ) : (
        <>
          <SellExisting stores={stores} onDone={loadMine}/>
          {user?.seller_type
            ? <NewListing stores={stores} categories={meta.categories} onDone={loadMine}/>
            : <p className="text-sm text-ink/60" data-testid="new-listing-locked">Adding brand-new products to the catalogue unlocks after your partner registration is approved.</p>}
        </>
      )}
      <section className="card-soft p-4" data-testid="my-national-listings">
        <div className="font-heading font-bold mb-2">My catalogue listings ({mine.length})</div>
        {mine.length === 0 ? <p className="text-sm text-ink/60">None yet.</p> : mine.map(m => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 border-t border-border py-2 text-sm">
            <span className="flex-1">{m.name} · <span className="text-ink/60">{m.store_name}</span></span>
            <span>₹{m.price} · {m.stock} in stock · SKU {m.sku}</span>
            <span className={`font-semibold capitalize ${STATUS_CLS[m.listing?.status] || ''}`}>{m.listing?.status}</span>
            {m.listing?.status === 'approved' && <Link to={`/p/${m.listing.slug}`} className="underline text-xs">View</Link>}
            {m.listing?.admin_note && <span className="w-full text-xs text-ink/60">Note: {m.listing.admin_note}</span>}
          </div>
        ))}
      </section>
    </div>
  );
}
